import { InboxOutlined } from '@ant-design/icons'
import { Input, theme } from 'antd'
import { t } from 'i18next'
import { ReactNode, useMemo } from 'react'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { AllowClearIcon } from '../../../components'
import BackButton from '../../../components/BackButton'
import BoldText from '../../../components/display/BoldText'
import {
  NO_DATA_PLACEHOLDER,
  algorithmRunStatuses,
  dateFormat,
} from '../../../constants/constants'
import { useAlgorithmRunDetails, useWorkspaceDetails } from '../../../hooks'
import SmartFunctionActionDropdown from './SmartFunctionActionDropdown/SmartFunctionActionDropdown'
import SmartFunctionSelectRun from './SmartFunctionSelectRun'

const { Search } = Input

export const SmartFunctionSubHeader = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 12px;
  gap: 16px;
`

export const SmartFunctionMainSubHeader = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

export const SmartFunctionSubHeaderLine = styled.div`
  display: flex;
  flex: 1;
  padding: 0 0 16px 0;
  gap: 16px;
  color: ${(props) => props.theme.token.colorText};
`

export const SmartFunctionSubHeaderCell = styled.div`
  flex: 50%;
  display: flex;
  align-items: center;
  gap: 16px;
`

export const SmartFunctionSubHeaderCellContainer = styled.div`
  flex: 50%;
  display: flex;
`

export const SmartFunctionSubHeaderCellSection = styled.div`
  flex: auto;
  color: ${(props) => props.theme.token.colorText};
`

const DateContainer = styled.div`
  margin-top: 8px;
`

const SelectRunContainer = styled.div`
  margin: 0;
`
const StatusContainer = styled.div`
  margin-top: 8px;
`

const LabelBlueContainer = styled.div`
  display: flex;
  padding: 1px 8px;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid var(--blue-3, #91caff);
  background: var(--blue-1, #e6f4ff);
  width: fit-content;
  color: black;
`

const TotalCellContainer = styled.div`
  display: flex;
  align-content: center;
  flex-wrap: wrap;
  gap: 8px;
`

export const SmartFunctionHeaderFirstRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
`

const { useToken } = theme

interface SmartFunctionHeaderLayoutProps {
  backButtonUrl: string
  totalFound: number
  hideTotalAndSearchLine?: boolean
  hideDataSource?: boolean
  onSearchAction?: (value: string) => void
  onSearchChange?: (event: any) => void
  onRunChange?: (value?: string) => void
  searchPlaceholder?: string
  secondLineStartChildren?: ReactNode
  secondLineEndChildren?: ReactNode

  children?: ReactNode
}

const SmartFunctionHeader = ({
  backButtonUrl,
  totalFound,
  hideTotalAndSearchLine = false,
  hideDataSource = false,
  onSearchAction,
  onSearchChange,
  onRunChange,
  searchPlaceholder,
  secondLineStartChildren,
  secondLineEndChildren,
  children,
}: SmartFunctionHeaderLayoutProps) => {
  const { workspaceId, algorithmRunId } = useParams()
  const { token } = useToken()
  const { selectedAlgorithmRun } = useAlgorithmRunDetails({
    algorithmRunId,
    workspaceId,
    preventFetch: true,
  })
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const algorithmRunIndex = useMemo(() => {
    return selectedAlgorithmRun
      ? (selectedWorkspace?.algorithmRuns
          ?.map((run) => run.id)
          .indexOf(selectedAlgorithmRun.id) || 0) + 1
      : 0
  }, [selectedAlgorithmRun, selectedWorkspace])

  return (
    <SmartFunctionSubHeader>
      <SmartFunctionMainSubHeader>
        <SmartFunctionHeaderFirstRow>
          <SmartFunctionSubHeaderLine>
            <SmartFunctionSubHeaderCell>
              <BackButton
                url={backButtonUrl}
                tooltipMessage={t('smartFunctionDetailsPage.backButtonTooltip')}
              />
              <BoldText>
                {selectedWorkspace?.algorithmName || NO_DATA_PLACEHOLDER}
              </BoldText>
            </SmartFunctionSubHeaderCell>
            <SmartFunctionSubHeaderCellContainer>
              <SmartFunctionSubHeaderCellSection>
                {selectedAlgorithmRun?.actionDate ? (
                  <DateContainer>
                    <Moment
                      local
                      date={selectedAlgorithmRun?.actionDate}
                      format={dateFormat.PRIMARY}
                    />
                  </DateContainer>
                ) : (
                  NO_DATA_PLACEHOLDER
                )}
              </SmartFunctionSubHeaderCellSection>
              {!!onRunChange ? (
                <SmartFunctionSubHeaderCellSection>
                  <SelectRunContainer>
                    <SmartFunctionSelectRun onChange={onRunChange} />
                  </SelectRunContainer>
                </SmartFunctionSubHeaderCellSection>
              ) : (
                <SmartFunctionSubHeaderCellSection>
                  {t('smartFunctionDetailsPage.runCount', {
                    count: algorithmRunIndex || 0,
                  })}
                </SmartFunctionSubHeaderCellSection>
              )}
              <SmartFunctionSubHeaderCellSection>
                <StatusContainer>
                  {selectedAlgorithmRun?.runStatus
                    ? t(
                        `workspaceDetailsPage.status.${selectedAlgorithmRun?.runStatus}`,
                      )
                    : NO_DATA_PLACEHOLDER}
                </StatusContainer>
              </SmartFunctionSubHeaderCellSection>
            </SmartFunctionSubHeaderCellContainer>
            <SmartFunctionActionDropdown
              key={selectedAlgorithmRun?.id}
              algorithmRun={selectedAlgorithmRun}
            />
          </SmartFunctionSubHeaderLine>
        </SmartFunctionHeaderFirstRow>
        <SmartFunctionSubHeaderLine>
          <SmartFunctionSubHeaderCell>
            {!!secondLineStartChildren && <>{secondLineStartChildren}</>}
            {!hideDataSource && (
              <>
                <LabelBlueContainer>
                  {selectedWorkspace?.dataSource.title || NO_DATA_PLACEHOLDER}
                </LabelBlueContainer>
              </>
            )}
          </SmartFunctionSubHeaderCell>
          <SmartFunctionSubHeaderCell>
            {selectedAlgorithmRun?.actionBy?.displayName || NO_DATA_PLACEHOLDER}
          </SmartFunctionSubHeaderCell>
          {!!secondLineEndChildren && <>{secondLineEndChildren}</>}
        </SmartFunctionSubHeaderLine>
        {selectedAlgorithmRun?.runStatus === algorithmRunStatuses.COMPLETE &&
          !hideTotalAndSearchLine && (
            <SmartFunctionSubHeaderLine>
              {children}
              <TotalCellContainer>
                <InboxOutlined />
                <div>
                  {totalFound} {t('Found')}
                </div>
              </TotalCellContainer>
              <Search
                placeholder={
                  searchPlaceholder ||
                  t('smartFunctionDetailsPage.defaultSearchPlaceholder') ||
                  ''
                }
                allowClear={{
                  clearIcon: <AllowClearIcon />,
                }}
                onSearch={onSearchAction}
                onChange={onSearchChange}
                style={{ flex: 1, backgroundColor: token.colorBgBase }}
              />
            </SmartFunctionSubHeaderLine>
          )}
      </SmartFunctionMainSubHeader>
    </SmartFunctionSubHeader>
  )
}

export default SmartFunctionHeader
